import { AxiosError, AxiosResponse } from 'axios'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { formatDate } from 'common/utils/date'
import { errorMessageByField } from 'common/utils/hook-form'
import endOfToday from 'date-fns/endOfToday'
import parseISO from 'date-fns/parseISO'
import {
  useMutateCreateGrowthShrimp,
  useMutatePatchGrowthShrimp,
} from 'modules/growth/api/mutation'
import { useGetGrowthShrimpLineChart, useGetGrowthShrimpToday } from 'modules/growth/api/queries'
import { useFindGrowthShrimpById, useGrowthShrimps } from 'modules/growth/api/selector'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { Box, Button, FormControl, FormHelperText, InputAdornment, TextField, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useGetPondNameById, useSpeciesName } from 'modules/pond/api/selector'
import { useParams } from 'react-router'
import { SpeciesType } from 'modules/pond/constants'
import { fNumber } from 'common/utils/formatNumber'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { normalizeSpeciesName } from 'utils/analytics'

interface GrowthShrimpDialogProps {
  onClose: () => void
  open: boolean
  isEdit?: boolean
}

interface FormDataType {
  date: Date
  amount: number | undefined
}

const GrowthShrimpDialog = ({ onClose, open, isEdit }: GrowthShrimpDialogProps) => {
  const { t } = useTranslation('growth')
  const [submitting, setSubmitting] = useState(false)
  const { refetch } = useGetGrowthShrimpLineChart()
  const { refetch: refetchTable } = useGrowthShrimps()
  const { refetch: refetchTodayCard } = useGetGrowthShrimpToday()
  const growthShrimpData = useFindGrowthShrimpById()
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')

  const recordGrowthShrimpMutation = useMutateCreateGrowthShrimp()
  const patchGrowthShrimpMutation = useMutatePatchGrowthShrimp()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const schema = Yup.object().shape({
    date: Yup.date(),
    amount: Yup.number()
      .typeError(t('require.number'))
      .positive(t('require.number'))
      .required(t('require.number')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm<FormDataType>({
    defaultValues: {
      date: new Date(),
      amount: undefined,
    },
    resolver,
  })

  useEffect(() => {
    if (growthShrimpData && isEdit) {
      reset((_values) => ({
        date: parseISO(growthShrimpData.date),
        amount: growthShrimpData.shrimpCountPerKg,
      }))
    }
  }, [growthShrimpData, isEdit])

  const onSuccess = (res: AxiosResponse<any>) => {
    const { status } = res.data
    if (status === 200) {
      setSubmitting(false)
      onClose()
      reset()
      setTimeout(() => {
        refetch()
        refetchTable()
        refetchTodayCard()
      }, 1000)
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(
        isEdit ? AnalyticsEvents.growthRecordUpdated : AnalyticsEvents.growthRecordCreated,
        normalizedSpecies ? { species: normalizedSpecies } : undefined
      )
    }
  }

  const onError = (error: unknown, _variables: any, _context: unknown) => {
    const { response } = error as AxiosError
    if (response?.status === 400) {
      setError('date', {
        type: 'server',
        message: t('duplicate.date'),
      })
    }
  }

  const onSubmit = async (data: FormDataType) => {
    setSubmitting(true)
    const formattedDate = formatDate(data.date)
    const amount = Number(data.amount)
    if (isEdit) {
      await patchGrowthShrimpMutation
        .mutateAsync(
          {
            growthId: Number(growthShrimpData?.id),
            body: {
              date: formattedDate,
              shrimpCountPerKg: amount,
            },
          },
          { onSuccess, onError }
        )
        .finally(() => setSubmitting(false))
      return
    }
    await recordGrowthShrimpMutation
      .mutateAsync(
        {
          body: {
            date: formattedDate,
            shrimpCountPerKg: amount,
          },
        },
        { onSuccess, onError }
      )
      .finally(() => setSubmitting(false))
  }

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      formId="record-growthshrimp"
      title={t('record.growth')}
      isSubmitting={submitting}
    >
      <form id="record-growthshrimp" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} sx={{ width: '100%' }}>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography component="label" sx={{ fontWeight: 700 }}>
                <span style={{ color: '#d32f2f', marginRight: 6 }}>*</span>
                {t('date')}
              </Typography>
            </Stack>

            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <ThaiDatePicker {...field} format="dd/MM/yyyy" maxDate={endOfToday()} slotProps={{ textField: { id: 'growth-shrimp-date-picker', size: 'small', error: !!errors.date, helperText: errorMessageByField('date', errors), fullWidth: true, }, }} sx={{ width: '100%' }} readOnly={isEdit} />
              )}
            />
          </Box>

          <Box>
            <Typography component="label" sx={{ fontWeight: 700 }}>
              <span style={{ color: '#d32f2f', marginRight: 6 }}>*</span>
              {t('amount')}
            </Typography>

            <Controller
              name="amount"
              control={control}
              render={({ field }) => {
                const unitText =
                  pond?.species?.id === SpeciesType.Fish ? t('gram.per.one') : t('one.per.kg');

                const amountNumber = Number(field.value)
                const showHint = !Number.isNaN(amountNumber) && amountNumber > 0

                const perUnitGram =
                  pond?.species?.id === SpeciesType.Shrimp && showHint
                    ? 1000 / amountNumber
                    : null
                return (
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <TextField
                      {...field}
                      id="growth-shrimp-amount"
                      placeholder={t('fill.amount')}
                      error={!!errors.amount}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          height: 48,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ mr: 0 }}>
                            <Box
                              sx={{
                                px: 2,
                                py: 1,
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                fontSize: 14,
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                              }}
                            >
                              {unitText}
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {!!errors.amount && (
                      <FormHelperText error>{errorMessageByField('amount', errors)}</FormHelperText>
                    )}
                    {perUnitGram !== null && (
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5 }}
                        color="text.secondary"
                      >
                        ≈ {fNumber(perUnitGram)} {t('weight.per.shrimp')}
                      </Typography>
                    )}
                  </FormControl>
                );
              }}
            />
          </Box>
        </Stack>
      </form>
    </BaseRecordDialog>
  )
}

export default GrowthShrimpDialog
